import { useCallback, useEffect, useRef, useState } from 'react';

import { notificationsSocketService } from './notifications-socket.service';
import { NotificationsServerToClientEvents, NotificationsSocket } from './types';

export const useNotificationsSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<NotificationsSocket | null>(null);

  const handleConnect = useCallback(() => {
    setIsConnected(true);
  }, []);

  const handleDisconnect = useCallback(() => {
    setIsConnected(false);
  }, []);

  const connect = () => {
    try {
      const socket = notificationsSocketService.connect();
      socketRef.current = socket;
      setError(null);

      // Set up event listeners
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);

      // Set initial connection state
      setIsConnected(socket.connected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      // Remove event listeners
      socketRef.current.off('connect', handleConnect);
      socketRef.current.off('disconnect', handleDisconnect);
    }

    notificationsSocketService.disconnect();
    socketRef.current = null;
  };

  const on = <K extends keyof NotificationsServerToClientEvents>(
    event: K,
    listener: NotificationsServerToClientEvents[K],
  ) => {
    notificationsSocketService.on(event, listener);
  };

  const off = <K extends keyof NotificationsServerToClientEvents>(
    event: K,
    listener?: NotificationsServerToClientEvents[K],
  ) => {
    notificationsSocketService.off(event, listener);
  };

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    error,
    connect,
    disconnect,
    on,
    off,
  };
};
